import { useMemo } from 'react'
import { formatTime, type FrameRate } from '@easyedit/timeline'

/**
 * Régua de tempo.
 *
 * O intervalo entre marcas é escolhido pelo zoom, dentro de uma escala fixa. Deixar o
 * intervalo livre produziria marcas em 0,37s — ilegíveis. A escala vai de frames a
 * minutos para cobrir tanto o corte fino quanto um podcast de duas horas.
 */
const STEPS = [0.04, 0.1, 0.25, 0.5, 1, 2, 5, 10, 15, 30, 60, 120, 300, 600]

export function Ruler({
  zoom,
  duration,
  width,
  frameRate,
  onSeek,
  onSetRange,
}: {
  zoom: number
  duration: number
  width: number
  frameRate: FrameRate
  onSeek: (time: number) => void
  /** Shift+arrasto marca um intervalo em vez de mover o playhead. */
  onSetRange: (range: { start: number; end: number } | null) => void
}) {
  const ticks = useMemo(() => {
    // Alvo: uma marca a cada ~90px. Mais denso vira ruído, mais esparso perde referência.
    const target = 90 / zoom
    const step = STEPS.find((s) => s >= target) ?? STEPS[STEPS.length - 1]!
    const count = Math.ceil(Math.max(duration, width / zoom) / step) + 1
    return Array.from({ length: count }, (_v, i) => i * step)
  }, [zoom, duration, width])

  return (
    <div
      className="relative h-7 border-b border-ink-750 bg-ink-850 cursor-pointer select-none shrink-0"
      style={{ width }}
      onPointerDown={(event) => {
        const rect = event.currentTarget.getBoundingClientRect()
        const timeAt = (clientX: number) => Math.max(0, (clientX - rect.left) / zoom)

        if (event.shiftKey) {
          // Shift+arrasto marca um intervalo. É o gesto que liga DELETE_RANGE,
          // LIFT_RANGE e INSERT_GAP — operações que existem desde o começo e não
          // tinham nenhuma forma de serem pedidas pela interface.
          const anchor = timeAt(event.clientX)
          const drag = (e: PointerEvent) => {
            const current = timeAt(e.clientX)
            onSetRange({ start: Math.min(anchor, current), end: Math.max(anchor, current) })
          }
          const done = (e: PointerEvent) => {
            const current = timeAt(e.clientX)
            // Um shift+clique sem arrasto limpa o intervalo, em vez de criar um vazio.
            if (Math.abs(current - anchor) < 0.02) onSetRange(null)
            window.removeEventListener('pointermove', drag)
            window.removeEventListener('pointerup', done)
          }
          window.addEventListener('pointermove', drag)
          window.addEventListener('pointerup', done)
          return
        }

        const seek = (clientX: number) => onSeek(timeAt(clientX))
        seek(event.clientX)
        // Arrastar na régua faz scrub — comportamento esperado em qualquer editor.
        const onMove = (e: PointerEvent) => seek(e.clientX)
        const onUp = () => {
          window.removeEventListener('pointermove', onMove)
          window.removeEventListener('pointerup', onUp)
        }
        window.addEventListener('pointermove', onMove)
        window.addEventListener('pointerup', onUp)
      }}
    >
      {ticks.map((time) => (
        <div key={time} className="absolute top-0 bottom-0" style={{ left: time * zoom }}>
          <div className="absolute top-0 h-2 w-px bg-ink-600" />
          <span className="absolute top-2.5 left-1 font-mono text-2xs text-ink-500 tnum whitespace-nowrap">
            {formatTime(time, zoom > 60)}
          </span>
        </div>
      ))}
    </div>
  )
}
